import type { JSX } from 'react';

import { IntroScreenProps, SuccessScreenProps } from './LowLevelUnifiedAuth';
import { Account } from './UniversalAuthAccount';

export interface DataSyncSuccessScreenProps
  extends SuccessScreenProps<Account> {
  portalId: number;
  trackingSource?: string;
}

export interface DataSyncScreenOverrides {
  introScreen?: (props: IntroScreenProps) => JSX.Element | null;
  successScreen?: (props: DataSyncSuccessScreenProps) => JSX.Element;
}

const registry = new Map<number, DataSyncScreenOverrides>();

/**
 * Register custom intro/success screens for a DataSync app.
 *
 * Add registrations to `ui-universal-auth/static-1.9870/js/dataSyncRegistrations.ts`, which
 * is called once at app bootstrap via `initializeDataSyncRegistrations()`. Register
 * at init time, NOT inside a React component. Registration is stored in a
 * module-level singleton, so it persists for the full page session.
 *
 * @example
 * // In ui-universal-auth/static-1.9870/js/dataSyncRegistrations.ts
 * import { getQboAppId } from './constants/appIds';
 * import { QboIntroScreen } from './screens/qbo/QboIntroScreen';
 * import { QboSuccessScreen } from './screens/qbo/QboSuccessScreen';
 *
 * registerDataSyncScreens(getQboAppId(), {
 *   introScreen: (props) => <QboIntroScreen {...props} />,
 *   successScreen: (props) => <QboSuccessScreen {...props} />,
 * });
 */
export function registerDataSyncScreens(
  appId: number,
  overrides: DataSyncScreenOverrides
): void {
  registry.set(appId, overrides);
}

export function unregisterDataSyncScreens(appId: number): void {
  registry.delete(appId);
}

export function getDataSyncScreens(
  appId: number
): DataSyncScreenOverrides | undefined {
  return registry.get(appId);
}
